// src/features/leases/components/CriticalDateRow.jsx
//
// LSE8 — single critical date row in the activity section. Renders
// type tag (color-coded by bucket), date with days pill, category
// raw name, optional description (Notes), citations, and areas.
//
// EDIT-LEASE-MODAL-AUDIT: inline edit controls (description + due date)
// removed. The row is fully read-only; all edits route through EditLeaseModal.

import React, { useState } from "react";
import { T } from "../../../app/tokens.js";
import { DaysRemainingPill } from "./DaysRemainingPill.jsx";
import { ProvenanceBadge } from "./ProvenanceBadge.jsx";
import { parseDateOnly } from "../leaseTabConfig.js";

// Bucket → tag palette (mirrors server CHECK constraint order).
// Exported so LeaseDatesPane's BucketChip can reuse without duplication.
export const BUCKET_BG = {
  renewal_notice:    T.blueSoft,              // #E8F0FE
  break_option:      T.pillRedBg,             // #FCEBEB
  break_notice:      T.purpleBg,              // #F5F3FF
  expiration:        T.pillAmberBg,           // #FAEEDA
  escalation:        T.provenancePdfBg,       // #EEEDFE
  deposit_burndown:  T.pillGreenBg,           // #EAF3DE
  allowance_deadline:T.provenanceSpacebaseBg, // #E6F1FB
  allowance_warning: T.warmBeige,             // #F1EFE8
  allowance_expiry:  T.warmBeige,             // #F1EFE8
  cam_audit:         T.blueSoft,              // #E8F0FE
  other:             T.warmBeige,             // #F1EFE8
};
export const BUCKET_FG = {
  renewal_notice:    T.spacebaseBlue,  // INC-06
  break_option:      T.breakRed,       // #991B1B
  break_notice:      T.purple,         // #8B5CF6
  expiration:        T.expirationAmber,// #7A5C1E
  escalation:        T.provenancePdf,  // #534AB7
  deposit_burndown:  T.forestGreen,    // #3B6D11
  allowance_deadline:T.spacebaseBlue,  // INC-06
  allowance_warning: T.mid,    // #5F5E5A
  allowance_expiry:  T.mid,    // #5F5E5A
  cam_audit:         T.spacebaseBlue,  // INC-06
  other:             T.mid,    // #5F5E5A
};

// Human-readable label from bucket slug.
export const BUCKET_LABEL = {
  renewal_notice:   "Renewal",
  break_option:     "Break",
  break_notice:     "Break Notice Deadline",
  expiration:       "Expiry",
  escalation:       "Escalation",
  deposit_burndown: "Deposit",
  allowance_deadline:"TI Deadline",
  allowance_warning: "TI Warning",
  allowance_expiry:  "TI Expiry",
  cam_audit:         "CAM Audit",
  other:             "Other",
};

function fmtDate(d) {
  if (!d) return null;
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function daysFrom(dateStr) {
  if (!dateStr) return null;
  const dt = parseDateOnly(dateStr);
  if (!dt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}


export function CriticalDateRow({ cd, stale = false }) {
  // FLAG-STALE-CDS: cd.needsReview is persisted server-side so the amber
  // indicator survives page reloads. The caller may also pass stale=true
  // immediately after a PATCH response (before the next refetch). Either
  // signal is sufficient to show the border.
  const isStale = stale || !!cd.needsReview;
  const {
    type,
    dueDate,
    description,
    categoryRaw,
    citations,
    areas,
    fieldSources,
  } = cd;

  // LEASE-TAB-FIXES-4: collapsed by default — show only the header line (type
  // badge, category, date, days pill). Description/citations are revealed on click.
  // Rows with no body content (no description, no citations) stay flat (no toggle).
  const hasBody = !!(description || citations);
  const [collapsed, setCollapsed] = useState(true);

  const days = daysFrom(dueDate);
  const bg = BUCKET_BG[type] || BUCKET_BG.other;
  const fg = BUCKET_FG[type] || BUCKET_FG.other;
  const bucketLabel = BUCKET_LABEL[type] || "Other";
  const badgeText = type === "other"
    ? (description || categoryRaw || null)
    : bucketLabel;
  const label = bucketLabel;

  // Provenance: if this came from CSV import, badge as spacebase.
  const source = fieldSources ? "spacebase" : undefined;

  // ── Collapsed: one-line summary ───────────────────────────────────────────
  if (collapsed) {
    const collapsedLabel = type === "other"
      ? (categoryRaw || description || "Other")
      : label;
    return (
      <div
        data-testid="critical-date-row"
        data-stale={isStale || undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 0",
          borderBottom: `1px solid ${T.border}`,
          ...(isStale ? { borderLeft: `3px solid ${T.warnYellowBorder}`, paddingLeft: 8 } : {}),
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
            <span
              data-testid="cd-due-date-wrapper"
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <span
                data-testid="cd-due-date"
                style={{ fontSize: T.fsSm, color: T.mid }}
              >
                {fmtDate(dueDate)}
              </span>
            </span>

            <span style={{ color: T.mid, fontSize: T.fsSm }}>·</span>

            <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}>
              {collapsedLabel}
            </span>

            {hasBody && (
              <button
                type="button"
                data-testid="cd-expand-toggle"
                aria-expanded={false}
                onClick={() => setCollapsed(false)}
                title="Show clause text"
                style={{
                  background: "none",
                  border: "none",
                  padding: "0 2px",
                  cursor: "pointer",
                  color: T.mid,
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded: full layout ──────────────────────────────────────────────────
  return (
    <div
      data-testid="critical-date-row"
      data-stale={isStale || undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 0",
        borderBottom: `1px solid ${T.border}`,
        ...(isStale ? { borderLeft: `3px solid ${T.warnYellowBorder}`, paddingLeft: 8 } : {}),
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* ── Header line ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {badgeText && (
            <span
              data-testid="cd-type-badge"
              style={{
                fontSize: T.fsXxs,
                fontWeight: T.fwBold,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                padding: "1px 6px",
                borderRadius: 4,
                background: bg,
                color: fg,
                whiteSpace: "nowrap",
              }}
            >
              {badgeText}
            </span>
          )}

          <span
            data-testid="cd-title-wrapper"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
              {type === "other" ? (description || categoryRaw || null) : (categoryRaw || label)}
            </span>
          </span>

          <DaysRemainingPill days={days} />
          {source && <ProvenanceBadge source={source} />}

          {hasBody && (
            <button
              type="button"
              data-testid="cd-expand-toggle"
              aria-expanded={true}
              onClick={() => setCollapsed(true)}
              title="Hide clause text"
              style={{
                background: "none",
                border: "none",
                padding: "0 2px",
                cursor: "pointer",
                color: T.mid,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                marginLeft: "auto",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ transform: "rotate(180deg)" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>

        {/* ── Date / areas line ───────────────────────────────────────── */}
        <div style={{ marginTop: 2, fontSize: T.fsSm, color: T.mid, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span
            data-testid="cd-due-date-wrapper"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <span data-testid="cd-due-date">
              {fmtDate(dueDate)}
            </span>
          </span>
          {areas && <span data-testid="cd-areas">{areas}</span>}
        </div>

        {/* ── Body: description and citations ─────────────────────────── */}
        {description && (
          <div style={{ marginTop: 4, fontSize: T.fsSm, color: T.mid, whiteSpace: "pre-wrap" }}>
            {description}
          </div>
        )}

        {citations && (
          <div style={{ marginTop: 2, fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
            Citations: {citations}
          </div>
        )}
      </div>
    </div>
  );
}
